import dayjs from "dayjs";

export const buildSatisfactionAnalyticsParams = ({
  hotelId,
  selectedMonth,
  dateRange = ["", ""],
}) => {
  if (!hotelId) {
    return null;
  }

  const [fromDate, toDate] = dateRange;

  if (fromDate && toDate) {
    return {
      hotelId,
      fromDate,
      toDate,
    };
  }

  return {
    hotelId,
    monthYear: selectedMonth ? dayjs(selectedMonth).format("MM/YYYY") : dayjs().format("MM/YYYY"),
  };
};

export const mapRatingAnalytics = (ratingStats = []) =>
  ratingStats.map((item) => ({
    questionId: item.questionId,
    question: item.questionText,
    type: "rating_scale",

    average: item.overallAvg || 0,
    responses: item.totalCount || 0,
    change: null,

    labels: (item.series || []).map((point) => point.dateKey),

    trend: (item.series || []).map((point) => point.avg),

    series: item.series || [],
  }));

export const mapChoiceAnalytics = (multipleChoiceStats = []) =>
  multipleChoiceStats.map((item) => ({
    questionId: item.questionId,
    question: item.questionText,
    type: "multiple_choice",

    responses: item.totalAnswered || 0,

    options: (item.options || []).map((option) => ({
      optionId: option.optionId,
      label: option.label,
      count: option.count || 0,
      percentage: option.pct || 0,
    })),
  }));

export const mapTextAnalytics = ({ recentTextAnswers = [], textSummary = null }) => {
  const positiveTopics = textSummary?.positiveTopics || [];

  const complaintTopics = textSummary?.complaintTopics || [];

  return recentTextAnswers.map((item) => ({
    questionId: item.questionId,
    question: item.questionText,
    type: "text_input",

    responses: item.totalResponses || 0,

    commonThemes: [
      ...positiveTopics.map((topic) => ({
        label: topic.label,
        mentions: topic.count,
      })),

      ...complaintTopics.map((topic) => ({
        label: topic.label,
        mentions: topic.count,
      })),
    ],

    recentResponses: item.recent || [],
  }));
};

export const mapTextFeedbackSummary = (textSummary) => {
  if (!textSummary) {
    return null;
  }

  const goodCount = textSummary?.good?.count || 0;
  const badCount = textSummary?.bad?.count || 0;

  return {
    totalTextResponses: goodCount + badCount,

    sentiment: {
      good: goodCount,
      poor: badCount,

      goodPercentage: textSummary?.good?.pct || 0,
      poorPercentage: textSummary?.bad?.pct || 0,
    },

    change: {
      goodPercentageChange: null,
      poorPercentageChange: null,
    },

    topGoodReasons: (textSummary?.positiveTopics || []).map((topic) => ({
      label: topic.label,
      count: topic.count,
    })),

    topPoorReasons: (textSummary?.complaintTopics || []).map((topic) => ({
      label: topic.label,
      count: topic.count,
    })),

    recentPoorResponses: textSummary?.recentPoorFeedback || [],
  };
};

export const formatSatisfactionDelta = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) {
    return null;
  }

  return `${deltaPct > 0 ? "+" : ""}${deltaPct}%`;
};

export const getSatisfactionDeltaColor = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) {
    return "primary";
  }

  return deltaPct < 0 ? "error" : "success";
};

export const mapSatisfactionQuestions = ({ questions = [], hasForm = false }) =>
  questions.map((question, questionIndex) => {
    const questionType = question.questionType || question.type;

    const mappedQuestion = {
      questionText: question.questionText || question.question || "",

      questionType,
      isActive: question.isActive ?? true,
      sortOrder: questionIndex,
    };

    const existingQuestionId = question.questionId || question.id;

    if (
      hasForm &&
      existingQuestionId &&
      !String(existingQuestionId).startsWith("temp_") &&
      !String(existingQuestionId).startsWith("question_")
    ) {
      mappedQuestion.questionId = existingQuestionId;
    }

    if (questionType === "multiple_choice") {
      mappedQuestion.options = (question.options || []).map((option, optionIndex) => {
        if (typeof option === "string") {
          return {
            label: option,
            isActive: true,
            sortOrder: optionIndex,
          };
        }

        const mappedOption = {
          label: option.label || "",
          isActive: option.isActive ?? true,
          sortOrder: optionIndex,
        };

        const existingOptionId = option.optionId || option.id;

        if (
          hasForm &&
          existingOptionId &&
          !String(existingOptionId).startsWith("temp_") &&
          !String(existingOptionId).startsWith("option_")
        ) {
          mappedOption.optionId = existingOptionId;
        }

        return mappedOption;
      });
    }

    return mappedQuestion;
  });
